import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import { endPoints } from "../../constant/Environment";
import { addData, getAllData, deleteData } from "../../Utility/API";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import DeleteModel from "../../components/DeleteModel";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import { tokens } from "../../theme";
import { mono, th, td, TablePaginationActions } from "../../components/tableKit";

// Panel treatment used across src/pages. Flat surface rather than the card
// gradient, so the form fields sit on an even background top to bottom.
const dialogPaperSx = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: "12px",
  backgroundImage: "none",
};

// Match the legacy MUI-table screens sized by skin.css at 11px headers /
// 13.5px body, the same bump CoinPackage makes.
const thCell = { ...th, fontSize: 11, letterSpacing: 0.6 };
const tdCell = { ...td, fontSize: 13.5 };

const fieldSx = { "& .MuiInputBase-root": { fontSize: 13 } };

const ghostBtnSx = {
  textTransform: "none",
  fontSize: 12.5,
  color: tokens.textDim,
  borderColor: tokens.border,
  "&:hover": { borderColor: tokens.border2 },
};

function DialogHeader({ title, onClose }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Typography variant="h6" sx={{ fontSize: 16 }}>
        {title}
      </Typography>
      <IconButton size="small" onClick={onClose} aria-label="close">
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// Label above a control, matching the uppercase micro-labels used on the
// redesigned pages.
function Field({ label, children, span = 6 }) {
  return (
    <Box sx={{ gridColumn: { xs: "span 12", md: `span ${span}` } }}>
      <Typography
        sx={{
          color: tokens.textDim,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 700,
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// Rows can arrive in either shape: the multi-reward columns (sc_coin/gc_coin/
// freeSpin/booster) or the older single rewardType + rewardValue pair. Read both
// so existing data still renders while the backend is mid-migration.
const deriveRewards = (row) => {
  const legacyValue = Number(row?.rewardValue) || 0;
  const type = row?.rewardType;
  const pick = (column, legacyType) => {
    const value = Number(row?.[column]);
    if (Number.isFinite(value) && value > 0) return value;
    return type === legacyType ? legacyValue : 0;
  };
  return {
    sc: pick("sc_coin", "SAGA_COIN"),
    gc: pick("gc_coin", "GOLD_COIN"),
    freeSpin: pick("freeSpin", "FREE_SPIN"),
    booster: pick("booster", "BOOSTER"),
    // Gift cards have no dedicated column, so they only ever live in the
    // legacy pair and cannot be combined with the rewards above.
    giftCard: type === "GIFT_CARD" ? legacyValue : 0,
  };
};

// Numeric reward cell: dim dash when the reward is not part of the day.
const rewardCell = (value, color) =>
  value > 0 ? (
    <span style={{ fontFamily: mono, color }}>{value}</span>
  ) : (
    <span style={{ color: tokens.textDim2 }}>—</span>
  );

const MonthlyPromotions = () => {
  const [editData, setEditData] = useState({});

  // Form states
  const [day, setDay] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSpecial, setIsSpecial] = useState("");
  const [occasion, setoccasion] = useState("");
  const [multiplier, setMultiplier] = useState("");

  // Multi-reward per day: any combination of SC + GC + Free Spins + Booster.
  const [scChecked, setScChecked] = useState(false);
  const [gcChecked, setGcChecked] = useState(false);
  const [fsChecked, setFsChecked] = useState(false);
  const [boosterChecked, setBoosterChecked] = useState(false);
  const [giftChecked, setGiftChecked] = useState(false);
  const [scValue, setScValue] = useState("");
  const [gcValue, setGcValue] = useState("");
  const [freespinValue, setFreespinValue] = useState("");
  const [boosterValue, setBoosterValue] = useState("");
  const [giftValue, setGiftValue] = useState("");
  const [boosterPackageId, setBoosterPackageId] = useState("");
  const [packagelist, setPackagelist] = useState([]);

  // Streak-type plans: filter the table, size the Day dropdown, and tag new rewards.
  const [streakTypes, setStreakTypes] = useState([]);
  const [streakFilter, setStreakFilter] = useState("");
  const [streakTypeField, setStreakTypeField] = useState("");

  // A plan's streak length drives how many days the Day dropdown offers.
  const selectedPlanId = streakTypeField || streakFilter;
  const dayCount =
    Number(
      streakTypes.find((s) => String(s?.id) === String(selectedPlanId))?.streak
    ) || 30;

  // Promotion configuration (min / retain package)
  const [openconfig, setOpenconfig] = useState(false);
  const [minpackage, setMinpackage] = useState("");
  const [retainpacakage, setRetainpackage] = useState("");
  const [minpkvalue, setMinpkvalueValue] = useState("");
  const [retainpkvalue, setRetainpkValue] = useState("");

  // Table states
  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Delete reward
  const [rowDataToDelete, setRowDataToDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const resetForm = () => {
    setDay("");
    setTitle("");
    setDescription("");
    setIsSpecial("");
    setoccasion("");
    setMultiplier("");
    setScChecked(false);
    setGcChecked(false);
    setFsChecked(false);
    setBoosterChecked(false);
    setGiftChecked(false);
    setScValue("");
    setGcValue("");
    setFreespinValue("");
    setBoosterValue("");
    setGiftValue("");
    setBoosterPackageId("");
    setStreakTypeField("");
  };

  const handleClose = () => {
    setShow(false);
    setEditData({});
    resetForm();
  };

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchStreakTypes();
    fetchPackages();
    getconfigList();
  }, []);

  useEffect(() => {
    fetchMonthlyPromotions();
    setPage(0);
  }, [streakFilter]);

  const fetchPackages = async () => {
    try {
      const response = await getAllData(`${endPoints.api.GET_PACKAGE}`);
      const data = Array.isArray(response) ? response : response?.data || [];
      // Keep only live packages; rows without the flag are treated as live.
      setPackagelist(data.filter((item) => item?.isactive !== 0));
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const fetchStreakTypes = async () => {
    try {
      const response = await getAllData(`${endPoints.api.GET_ALL_STREAK_TYPES}`);
      const data = Array.isArray(response) ? response : response?.data || [];
      setStreakTypes(data);
      // Default the view to the 3-day streak (fallback to the first plan available).
      const defaultPlan = data.find((s) => Number(s?.streak) === 3) || data[0];
      if (defaultPlan?.id != null) {
        setStreakFilter(String(defaultPlan.id));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMonthlyPromotions = async () => {
    setIsLoading(true);
    try {
      // No filter -> all rewards. Filter -> rewards for that streak type.
      const url = streakFilter
        ? `${endPoints.api.GET_REWARDS_BY_STREAK_TYPE}${streakFilter}`
        : `${endPoints.api.MONTHLY_REWARDS}`;
      const response = await getAllData(url);
      if (Array.isArray(response)) {
        setRows(response);
      } else if (response?.status === "success") {
        setRows(response?.data || []);
      } else {
        ErrorToast("Failed to fetch monthly promotions data");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOk = async () => {
    const isEdit = !!editData?.id;

    // Creating a brand-new reward must be tagged to a streak type.
    if (!isEdit && !streakTypeField) {
      ErrorToast("Please select a Streak Type for the new reward");
      return;
    }
    if (!day) {
      ErrorToast("Please select a day");
      return;
    }

    const sc = scChecked ? Number(scValue) || 0 : 0;
    const gc = gcChecked ? Number(gcValue) || 0 : 0;
    const freeSpin = fsChecked ? Number(freespinValue) || 0 : 0;
    const booster = boosterChecked ? Number(boosterValue) || 0 : 0;
    const gift = giftChecked ? Number(giftValue) || 0 : 0;

    if (scChecked && sc <= 0) {
      ErrorToast("Enter a Sweeps Coins amount greater than 0");
      return;
    }
    if (gcChecked && gc <= 0) {
      ErrorToast("Enter a Gold Coins amount greater than 0");
      return;
    }
    if (fsChecked && freeSpin <= 0) {
      ErrorToast("Enter a Free Spins count greater than 0");
      return;
    }
    if (boosterChecked && (booster <= 0 || booster > 100)) {
      ErrorToast("Booster must be between 1 and 100 (%)");
      return;
    }
    if (giftChecked && gift <= 0) {
      ErrorToast("Enter a Gift Card value greater than 0");
      return;
    }
    if (sc <= 0 && gc <= 0 && freeSpin <= 0 && booster <= 0 && gift <= 0) {
      ErrorToast("Select at least one reward with a value greater than 0");
      return;
    }

    // Representative reward_type, kept for display + backend enum validation.
    let repType = "SAGA_COIN";
    if (gift > 0) repType = "GIFT_CARD";
    else if (sc > 0) repType = "SAGA_COIN";
    else if (gc > 0) repType = "GOLD_COIN";
    else if (freeSpin > 0) repType = "FREE_SPIN";
    else if (booster > 0) repType = "BOOSTER";

    setIsLoading(true);
    const url = isEdit
      ? `${endPoints.api.UPDATE_MONTHLY_REWARDS}`
      : `${endPoints.api.CREATE_MONTHLY_REWARDS}`;
    const payload = {
      dayNumber: Number(day),
      rewardType: repType,
      // Gift cards have no dedicated column, so they ride the legacy field.
      rewardValue: gift > 0 ? gift : 0,
      sc_coin: sc,
      gc_coin: gc,
      freeSpin: freeSpin,
      booster: booster,
      packageId: boosterChecked && boosterPackageId ? boosterPackageId : null,
      title: title,
      description: description,
      isSpecial: isSpecial === "Yes",
      active: true,
      occasion: occasion,
      multiplier: multiplier ? Number(multiplier) : 1,
    };
    if (isEdit) {
      payload.id = editData.id;
      payload.promotionType = Number(selectedPlanId) || undefined;
    } else {
      payload.streak_type = Number(streakTypeField);
      payload.promotionType = Number(streakTypeField);
    }
    try {
      const response = await addData(url, payload);
      if (response?.data?.status === "success") {
        SuccesToast(
          isEdit
            ? "monthly promotion updated successfully"
            : "reward created successfully"
        );
        fetchMonthlyPromotions();
        return;
      } else {
        ErrorToast(
          response?.data?.message ||
            response?.message ||
            "Failed to save monthly promotion"
        );
      }
    } catch (error) {
      console.error("Error saving monthly promotion:", error);
      ErrorToast("Error saving monthly promotion");
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  const deleteMonthlyReward = async () => {
    const id = rowDataToDelete?.id;
    setIsLoading(true);
    try {
      const response = await deleteData(
        `${endPoints.api.DELETE_MONTHLY_REWARDS}${id}`
      );
      if (response?.status === 200 || response?.data?.status === "success") {
        SuccesToast("reward deleted successfully");
        fetchMonthlyPromotions();
      } else {
        ErrorToast(response?.data?.message || "Failed to delete reward");
      }
    } catch (error) {
      console.error("Error deleting reward:", error);
      ErrorToast("Error deleting reward");
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    if (editData?.id) {
      setStreakTypeField(
        editData.streak_type != null ? String(editData.streak_type) : ""
      );
      setDay(editData.dayNumber || "");
      setTitle(editData.title || "");
      setDescription(editData.description || "");
      setIsSpecial(editData.isSpecial ? "Yes" : "No");
      setoccasion(editData.occasion || "");
      setMultiplier(editData.multiplier || "");

      const { sc, gc, freeSpin, booster, giftCard } = deriveRewards(editData);
      setScChecked(sc > 0);
      setScValue(sc > 0 ? sc : "");
      setGcChecked(gc > 0);
      setGcValue(gc > 0 ? gc : "");
      setFsChecked(freeSpin > 0);
      setFreespinValue(freeSpin > 0 ? freeSpin : "");
      setBoosterChecked(booster > 0);
      setBoosterValue(booster > 0 ? booster : "");
      setGiftChecked(giftCard > 0);
      setGiftValue(giftCard > 0 ? giftCard : "");

      if (booster > 0 && !editData.packageId) {
        // Legacy row created before per-package boosters: default to the first
        // daily-streak package so what's shown matches what Update would save.
        const dailyStreakPackages = packagelist.filter((pkg) => pkg?.dailyStreak);
        setBoosterPackageId(dailyStreakPackages[0]?.id || "");
      } else {
        setBoosterPackageId(editData.packageId || "");
      }
    }
  }, [editData]);

  const getconfigList = async () => {
    try {
      const url = `${endPoints.api.GET_MONTHLY_REWARD_CONFIG}`;
      const response = await getAllData(url);
      const data = Array.isArray(response) ? response : response?.data;
      if (data?.length) {
        setMinpackage(data[0]?.min_pk_id || "");
        setRetainpackage(data[0]?.max_pk_id || "");
        setMinpkvalueValue(data[0]?.minPackageAmount || "");
        setRetainpkValue(data[0]?.maxPackageAmount || "");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfigUpdate = async () => {
    setIsLoading(true);
    const url = `${endPoints.api.MONTHLY_REWARD_CONFIG}`;
    const payload = {
      minPackageAmount: minpkvalue || "",
      maxPackageAmount: retainpkvalue || "",
      min_pk_id: minpackage,
      max_pk_id: retainpacakage,
      packageType: "UNLOCK",
      description: "Monthly Promotion Package Configuration",
      isActive: true,
      id: 0,
    };
    try {
      const response = await addData(url, payload);
      if (response?.data?.status === "success") {
        SuccesToast("monthly promotion configuration updated successfully");
        getconfigList();
        return;
      } else {
        ErrorToast(
          response?.data?.message ||
            response?.message ||
            "Failed to update monthly promotion configuration"
        );
      }
    } catch (error) {
      console.error("Error updating monthly promotion configuration:", error);
      ErrorToast("Error updating monthly promotion configuration");
    } finally {
      setIsLoading(false);
      setOpenconfig(false);
    }
  };

  const planLabel = streakTypes.find(
    (s) => String(s?.id) === String(streakFilter)
  )?.streak;

  const dailyStreakPackages = packagelist.filter((pkg) => pkg?.dailyStreak);

  // Reward toggles rendered in the dialog. Gift cards have no column of their
  // own, so they cannot be combined with the rest.
  const rewardToggles = [
    {
      key: "sc",
      label: "Sweeps Coins (SC)",
      placeholder: "SC amount",
      checked: scChecked,
      setChecked: setScChecked,
      value: scValue,
      setValue: setScValue,
    },
    {
      key: "gc",
      label: "Gold Coins (GC)",
      placeholder: "GC amount",
      checked: gcChecked,
      setChecked: setGcChecked,
      value: gcValue,
      setValue: setGcValue,
    },
    {
      key: "fs",
      label: "Free Spins",
      placeholder: "Free spin count",
      checked: fsChecked,
      setChecked: setFsChecked,
      value: freespinValue,
      setValue: setFreespinValue,
    },
  ];

  return (
    <Box>
      <Toaster />
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <DeleteModel
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={deleteMonthlyReward}
      />

      <PageHeader
        title="Monthly Promotions"
        crumb="Promotions / Monthly Promotions"
        subtitle={
          isLoading
            ? "Loading promotions…"
            : `${rows.length} reward days${
                planLabel ? ` · ${planLabel}-day streak` : " · all streak types"
              }`
        }
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              sx={ghostBtnSx}
              startIcon={<TuneRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setOpenconfig(true)}
            >
              Configuration
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditData({});
                resetForm();
                setStreakTypeField(streakFilter || "");
                setShow(true);
              }}
            >
              Add Promotion
            </Button>
          </>
        }
      />

      <SectionCard sx={{ height: "auto" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            mb: 2,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <Select
              displayEmpty
              value={streakFilter}
              onChange={(event) => setStreakFilter(event.target.value)}
              input={<OutlinedInput />}
              sx={{ fontSize: 13 }}
              renderValue={(selected) =>
                selected === "" ? (
                  <span style={{ color: tokens.textDim }}>All streak types</span>
                ) : (
                  `${
                    streakTypes.find((s) => String(s?.id) === String(selected))
                      ?.streak
                  } Day Promotion`
                )
              }
            >
              <MenuItem value="">All streak types</MenuItem>
              {streakTypes.map((s) => (
                <MenuItem key={s?.id} value={String(s?.id)}>
                  {s?.streak} Day Promotion
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: 12, color: tokens.textDim }}>
            {`Rewards granted on each day of the streak · ${rows.length} days`}
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thCell, textAlign: "center" }}>Day</th>
                <th style={thCell}>Title</th>
                <th style={thCell}>Description</th>
                <th style={{ ...thCell, textAlign: "center" }}>SC</th>
                <th style={{ ...thCell, textAlign: "center" }}>GC</th>
                <th style={{ ...thCell, textAlign: "center" }}>Free Spins</th>
                <th style={{ ...thCell, textAlign: "center" }}>Booster %</th>
                <th style={{ ...thCell, textAlign: "center" }}>Gift Card</th>
                <th style={{ ...thCell, textAlign: "center" }}>Multiplier</th>
                <th style={{ ...thCell, textAlign: "center" }}>Special</th>
                <th style={thCell}>Occasion</th>
                <th style={{ ...thCell, textAlign: "center" }}>Options</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      ...tdCell,
                      textAlign: "center",
                      color: tokens.textDim,
                    }}
                  >
                    No promotions found for this streak type.
                  </td>
                </tr>
              )}

              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const reward = deriveRewards(row);
                  return (
                    <tr key={row.id || index}>
                      <td
                        style={{
                          ...tdCell,
                          textAlign: "center",
                          fontFamily: mono,
                          color: tokens.textDim,
                        }}
                      >
                        {row?.dayNumber}
                      </td>
                      <td style={{ ...tdCell, fontWeight: 600 }}>{row?.title}</td>
                      <td
                        style={{
                          ...tdCell,
                          color: tokens.textDim,
                          maxWidth: 260,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row?.description}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {rewardCell(reward.sc, tokens.secondary)}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {rewardCell(reward.gc, tokens.accent)}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {rewardCell(reward.freeSpin, tokens.blue)}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {rewardCell(reward.booster, tokens.primary)}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {rewardCell(reward.giftCard, tokens.pink)}
                      </td>
                      <td
                        style={{
                          ...tdCell,
                          textAlign: "center",
                          fontFamily: mono,
                        }}
                      >
                        {row?.multiplier ? `${row.multiplier}x` : "—"}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        {row?.isSpecial ? (
                          <span style={{ color: tokens.accent, fontWeight: 600 }}>
                            Yes
                          </span>
                        ) : (
                          <span style={{ color: tokens.textDim2 }}>No</span>
                        )}
                      </td>
                      <td style={{ ...tdCell, color: tokens.textDim }}>
                        {row?.occasion || "—"}
                      </td>
                      <td style={{ ...tdCell, textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-label="edit promotion"
                            onClick={() => {
                              setEditData(row);
                              setShow(true);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="delete promotion"
                            onClick={() => {
                              setRowDataToDelete(row);
                              setShowDeleteModal(true);
                            }}
                          >
                            <DeleteIcon
                              sx={{ fontSize: 17, color: tokens.danger }}
                            />
                          </IconButton>
                        </Box>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          sx={{ color: tokens.textDim, borderTop: `1px solid ${tokens.border}` }}
        />
      </SectionCard>

      {/* Create / edit promotion */}
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader
          title={
            editData?.id ? "Edit Monthly Promotion" : "Add Monthly Promotion"
          }
          onClose={handleClose}
        />

        <Box sx={{ p: 2.5, maxHeight: "72vh", overflowY: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 2,
            }}
          >
            <Field label="Streak Type">
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={streakTypeField}
                  disabled={!!editData?.id}
                  onChange={(event) => setStreakTypeField(event.target.value)}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>
                        Select streak type
                      </span>
                    ) : (
                      `${
                        streakTypes.find(
                          (s) => String(s?.id) === String(selected)
                        )?.streak
                      } Days`
                    )
                  }
                >
                  <MenuItem value="">Select streak type</MenuItem>
                  {streakTypes.map((s) => (
                    <MenuItem key={s?.id} value={String(s?.id)}>
                      {s?.streak} Days
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>

            <Field label="Day">
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={day}
                  onChange={(event) => setDay(event.target.value)}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>Select day</span>
                    ) : (
                      `Day ${selected}`
                    )
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">Select day</MenuItem>
                  {Array.from({ length: dayCount }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      Day {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>

            <Field label="Title">
              <TextField
                fullWidth
                size="small"
                placeholder="Enter title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Description">
              <TextField
                fullWidth
                size="small"
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Rewards for this day" span={12}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: "10px",
                  background: tokens.bg2,
                  p: 1.5,
                }}
              >
                {rewardToggles.map((toggle) => (
                  <Box key={toggle.key}>
                    <FormControlLabel
                      sx={{
                        mr: 0,
                        "& .MuiFormControlLabel-label": { fontSize: 13 },
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={toggle.checked}
                          disabled={giftChecked}
                          onChange={(e) => toggle.setChecked(e.target.checked)}
                        />
                      }
                      label={toggle.label}
                    />
                    {toggle.checked && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0 }}
                        placeholder={toggle.placeholder}
                        value={toggle.value}
                        onChange={(e) => toggle.setValue(e.target.value)}
                        sx={{ ...fieldSx, mt: 0.5 }}
                      />
                    )}
                  </Box>
                ))}

                <Box>
                  <FormControlLabel
                    sx={{
                      mr: 0,
                      "& .MuiFormControlLabel-label": { fontSize: 13 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={boosterChecked}
                        disabled={giftChecked}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setBoosterChecked(checked);
                          if (checked && !boosterPackageId) {
                            setBoosterPackageId(dailyStreakPackages[0]?.id || "");
                          }
                        }}
                      />
                    }
                    label="Booster (%)"
                  />
                  {boosterChecked && (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="Booster %"
                        value={boosterValue}
                        onChange={(e) => setBoosterValue(e.target.value)}
                        sx={{ ...fieldSx, mt: 0.5 }}
                      />
                      <FormControl fullWidth size="small" sx={{ mt: 0.75 }}>
                        <Select
                          displayEmpty
                          value={boosterPackageId}
                          onChange={(e) => setBoosterPackageId(e.target.value)}
                          input={<OutlinedInput />}
                          sx={{ fontSize: 13 }}
                          renderValue={(selected) =>
                            selected === "" ? (
                              <span style={{ color: tokens.textDim }}>
                                Select package
                              </span>
                            ) : (
                              `$${
                                dailyStreakPackages.find(
                                  (p) => String(p.id) === String(selected)
                                )?.purchase_price ?? ""
                              }`
                            )
                          }
                        >
                          {dailyStreakPackages.map((pkg) => (
                            <MenuItem key={pkg.id} value={pkg.id}>
                              ${pkg.purchase_price}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Box>

                <Box sx={{ gridColumn: { xs: "span 1", sm: "span 2" } }}>
                  <FormControlLabel
                    sx={{
                      mr: 0,
                      "& .MuiFormControlLabel-label": { fontSize: 13 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={giftChecked}
                        disabled={
                          scChecked || gcChecked || fsChecked || boosterChecked
                        }
                        onChange={(e) => setGiftChecked(e.target.checked)}
                      />
                    }
                    label="Gift Card"
                  />
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: tokens.textDim2, mb: 0.5 }}
                  >
                    Gift cards have no column of their own, so they cannot be
                    combined with the rewards above.
                  </Typography>
                  {giftChecked && (
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      placeholder="Gift card value"
                      value={giftValue}
                      onChange={(e) => setGiftValue(e.target.value)}
                      sx={{ ...fieldSx, maxWidth: 260 }}
                    />
                  )}
                </Box>
              </Box>
            </Field>

            <Field label="Multiplier" span={4}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={multiplier}
                  onChange={(event) => setMultiplier(event.target.value)}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>
                        Select multiplier
                      </span>
                    ) : (
                      `${selected}x`
                    )
                  }
                >
                  <MenuItem value="">Select multiplier</MenuItem>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <MenuItem key={value} value={String(value)}>
                      {value}x
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>

            <Field label="Occasion" span={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter occasion"
                value={occasion}
                onChange={(e) => setoccasion(e.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Special Reward" span={4}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={isSpecial}
                  onChange={(event) => setIsSpecial(event.target.value)}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>Select</span>
                    ) : (
                      selected
                    )
                  }
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Field>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${tokens.border}`,
          }}
        >
          <Button size="small" variant="outlined" sx={ghostBtnSx} onClick={handleClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleOk}>
            {editData?.id ? "Update Promotion" : "Create Promotion"}
          </Button>
        </Box>
      </Dialog>

      {/* Promotion configuration */}
      <Dialog
        open={openconfig}
        onClose={() => setOpenconfig(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader
          title="Monthly Promotion Configuration"
          onClose={() => setOpenconfig(false)}
        />

        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gap: 2,
            }}
          >
            <Field label="Minimum Package" span={12}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={minpackage}
                  onChange={(event) => {
                    const selectedId = event.target.value;
                    setMinpackage(selectedId);
                    const selectedPackage = packagelist.find(
                      (pkg) => String(pkg?.id) === String(selectedId)
                    );
                    setMinpkvalueValue(selectedPackage?.purchase_price);
                  }}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>
                        Select package
                      </span>
                    ) : (
                      `$${
                        packagelist.find(
                          (p) => String(p.id) === String(selected)
                        )?.purchase_price ?? ""
                      }`
                    )
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">Select package</MenuItem>
                  {packagelist.map((item) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      ${item?.purchase_price}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>

            <Field label="Retain Package" span={12}>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={retainpacakage}
                  onChange={(event) => {
                    const selectedId = event.target.value;
                    setRetainpackage(selectedId);
                    const selectedPackage = packagelist.find(
                      (pkg) => String(pkg?.id) === String(selectedId)
                    );
                    setRetainpkValue(selectedPackage?.purchase_price);
                  }}
                  input={<OutlinedInput />}
                  sx={{ fontSize: 13 }}
                  renderValue={(selected) =>
                    selected === "" ? (
                      <span style={{ color: tokens.textDim }}>
                        Select package
                      </span>
                    ) : (
                      `$${
                        packagelist.find(
                          (p) => String(p.id) === String(selected)
                        )?.purchase_price ?? ""
                      }`
                    )
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">Select package</MenuItem>
                  {packagelist.map((item) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      ${item?.purchase_price}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${tokens.border}`,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            sx={ghostBtnSx}
            onClick={() => setOpenconfig(false)}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleConfigUpdate}>
            Update Config
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default MonthlyPromotions;
